import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronRight, ChevronDown, CheckCircle2, AlertTriangle, XCircle, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

interface TreeNode {
  control_id: string;
  parent_control_id: string | null;
  clause_code: string;
  title: string;
  title_id: string | null;
  description: string | null;
  criticality: string;
  weight: number;
  sort_order: number;
  evidence_count: number;
  valid_evidence_count: number;
  expiring_evidence_count: number;
  pending_review_count: number;
  status: "compliant" | "partial" | "at_risk" | "gap";
  children?: TreeNode[];
}

const STATUS_META: Record<string, { label: string; cls: string; Icon: any }> = {
  compliant: { label: "Compliant", cls: "bg-success/10 text-success border-success/20", Icon: CheckCircle2 },
  partial:   { label: "Partial",   cls: "bg-warning/10 text-warning border-warning/20", Icon: AlertTriangle },
  at_risk:   { label: "At Risk",   cls: "bg-destructive/10 text-destructive border-destructive/20", Icon: AlertTriangle },
  gap:       { label: "Gap",       cls: "bg-muted text-muted-foreground border-border", Icon: XCircle },
};

interface Props {
  companyId: string;
  frameworkId: string;
  frameworks: { id: string; code: string; name: string; name_id: string | null }[];
  onFrameworkChange: (id: string) => void;
}

export default function ComplianceTreeView({ companyId, frameworkId, frameworks, onFrameworkChange }: Props) {
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!companyId || !frameworkId) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, frameworkId]);

  async function load() {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_compliance_tree", {
        p_company_id: companyId,
        p_framework_id: frameworkId,
        p_site_id: null,
      });
      if (error) throw error;
      setNodes((data || []) as TreeNode[]);
    } catch (e: any) {
      toast.error("Gagal memuat: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function runAutoLink() {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("compliance-auto-link", {
        body: { company_id: companyId, framework_id: frameworkId, lookback_days: 180, use_ai: true },
      });
      if (error) throw error;
      toast.success(`${data.links_created} link bukti dibuat (pending review). ${data.candidates_scanned} kandidat dipindai.`);
      await load();
    } catch (e: any) {
      toast.error("Gagal auto-link: " + e.message);
    } finally {
      setRunning(false);
    }
  }

  const tree = useMemo(() => buildTree(nodes), [nodes]);

  const summary = useMemo(() => {
    const total = nodes.length;
    const c = nodes.filter((n) => n.status === "compliant").length;
    const p = nodes.filter((n) => n.status === "partial").length;
    const a = nodes.filter((n) => n.status === "at_risk").length;
    const g = nodes.filter((n) => n.status === "gap").length;
    // Weighted score
    const totalW = nodes.reduce((s, n) => s + (n.weight || 1), 0);
    const earned = nodes.reduce((s, n) => {
      if (n.status === "compliant") return s + n.weight;
      if (n.status === "partial") return s + n.weight * 0.6;
      if (n.status === "at_risk") return s + n.weight * 0.3;
      return s;
    }, 0);
    const score = totalW > 0 ? Math.round((earned / totalW) * 100) : 0;
    return { total, c, p, a, g, score };
  }, [nodes]);

  function toggle(id: string) {
    setExpanded((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function expandAll() {
    setExpanded(new Set(nodes.filter((n) => nodes.some((c) => c.parent_control_id === n.control_id)).map((n) => n.control_id)));
  }
  function collapseAll() {
    setExpanded(new Set());
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={frameworkId} onValueChange={onFrameworkChange}>
            <SelectTrigger className="w-[260px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {frameworks.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.code} — {f.name_id || f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={expandAll}>Expand all</Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>Collapse all</Button>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-base">Score {summary.score}%</Badge>
          <Button onClick={runAutoLink} disabled={running} size="sm">
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {running ? "Memindai…" : "Run Auto-Link"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 text-center text-sm">
        <div className="border rounded p-2"><div className="text-xs text-muted-foreground">Compliant</div><div className="font-bold text-success">{summary.c}</div></div>
        <div className="border rounded p-2"><div className="text-xs text-muted-foreground">Partial</div><div className="font-bold text-warning">{summary.p}</div></div>
        <div className="border rounded p-2"><div className="text-xs text-muted-foreground">At Risk</div><div className="font-bold text-destructive">{summary.a}</div></div>
        <div className="border rounded p-2"><div className="text-xs text-muted-foreground">Gap</div><div className="font-bold text-muted-foreground">{summary.g}</div></div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground grid grid-cols-[1fr_120px_120px_140px] gap-2">
          <div>Klausul / Kontrol</div>
          <div className="text-center">Bukti (valid/total)</div>
          <div className="text-center">Pending</div>
          <div>Status</div>
        </div>
        {loading ? (
          <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-4 h-4 mr-2 inline animate-spin" />Loading…</div>
        ) : tree.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">Tidak ada kontrol.</div>
        ) : (
          <div>
            {tree.map((n) => <TreeRow key={n.control_id} node={n} depth={0} expanded={expanded} onToggle={toggle} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function TreeRow({ node, depth, expanded, onToggle }: { node: TreeNode; depth: number; expanded: Set<string>; onToggle: (id: string) => void }) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const open = expanded.has(node.control_id);
  const meta = STATUS_META[node.status] ?? STATUS_META.gap;
  const Icon = meta.Icon;
  return (
    <>
      <div
        className="px-3 py-2 grid grid-cols-[1fr_120px_120px_140px] gap-2 items-center border-t hover:bg-muted/30"
        style={{ paddingLeft: 12 + depth * 18 }}
      >
        <div className="flex items-center gap-1 min-w-0">
          {hasChildren ? (
            <button onClick={() => onToggle(node.control_id)} className="shrink-0 p-0.5 hover:bg-muted rounded">
              {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          ) : <span className="w-4" />}
          <span className="font-mono text-xs text-muted-foreground shrink-0">{node.clause_code}</span>
          <span className="text-sm truncate" title={node.title_id || node.title}>{node.title_id || node.title}</span>
          {node.criticality === "critical" && <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20 ml-1">critical</Badge>}
        </div>
        <div className="text-center text-sm">
          <span className="font-medium">{node.valid_evidence_count}</span>
          <span className="text-muted-foreground"> / {node.evidence_count}</span>
          {node.expiring_evidence_count > 0 && (
            <span className="ml-1 text-warning text-xs">⏳{node.expiring_evidence_count}</span>
          )}
        </div>
        <div className="text-center text-sm">
          {node.pending_review_count > 0 ? (
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">{node.pending_review_count}</Badge>
          ) : <span className="text-muted-foreground">—</span>}
        </div>
        <div>
          <Badge variant="outline" className={meta.cls}>
            <Icon className="w-3 h-3 mr-1" />{meta.label}
          </Badge>
        </div>
      </div>
      {open && node.children?.map((c) => (
        <TreeRow key={c.control_id} node={c} depth={depth + 1} expanded={expanded} onToggle={onToggle} />
      ))}
    </>
  );
}

function buildTree(flat: TreeNode[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  flat.forEach((n) => map.set(n.control_id, { ...n, children: [] }));
  const roots: TreeNode[] = [];
  for (const n of map.values()) {
    if (n.parent_control_id && map.has(n.parent_control_id)) {
      map.get(n.parent_control_id)!.children!.push(n);
    } else {
      roots.push(n);
    }
  }
  const sortRec = (arr: TreeNode[]) => {
    arr.sort((a, b) => a.sort_order - b.sort_order || a.clause_code.localeCompare(b.clause_code));
    arr.forEach((n) => n.children && sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}
